import { Row, Col, Card, Typography, Skeleton } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import postmanIcon from "../assets/images/postman-icon.png";

const { Title, Paragraph, Text } = Typography;

const escapeHtml = (value) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escapeAttribute = (value) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const renderInlineHtml = (value) => {
  let output = escapeHtml(value);
  output = output.replace(/`([^`]+)`/g, "<code>$1</code>");
  output = output.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  output = output.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  output = output.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, href) => {
    const safeHref = escapeAttribute(href);
    return `<a href="${safeHref}">${text}</a>`;
  });
  return { __html: output };
};

const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const parseTable = (lines) => {
  const rows = lines
    .map((line) => line.trim())
    .filter((line) => line.startsWith("|"))
    .map((line) =>
      line
        .replace(/^\|/, "")
        .replace(/\|$/, "")
        .split("|")
        .map((cell) => cell.trim())
    );

  if (rows.length < 2) {
    return null;
  }

  const header = rows[0];
  const body = rows.slice(2);

  return { header, body };
};

const parseMarkdown = (markdown) => {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line || line.trim() === "") {
      i += 1;
      continue;
    }

    if (line.startsWith("```")) {
      const language = line.slice(3).trim();
      const codeLines = [];
      i += 1;
      while (i < lines.length && !lines[i].startsWith("```")) {
        codeLines.push(lines[i]);
        i += 1;
      }
      i += 1;
      blocks.push({ type: "code", language, content: codeLines.join("\n") });
      continue;
    }

    if (/^#{1,6}\s/.test(line)) {
      const level = line.match(/^#{1,6}/)[0].length;
      const text = line.replace(/^#{1,6}\s*/, "");
      blocks.push({ type: "heading", level, text });
      i += 1;
      continue;
    }

    if (/^---+$/.test(line.trim())) {
      blocks.push({ type: "hr" });
      i += 1;
      continue;
    }

    if (/^(\*|-)\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^(\*|-)\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^(\*|-)\s+/, ""));
        i += 1;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    if (line.trim().startsWith("|")) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i]);
        i += 1;
      }
      const table = parseTable(tableLines);
      if (table) {
        blocks.push({ type: "table", ...table });
      } else {
        blocks.push({ type: "p", text: tableLines.join(" ") });
      }
      continue;
    }

    const paragraphLines = [line];
    i += 1;
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !lines[i].startsWith("```") &&
      !/^#{1,6}\s/.test(lines[i]) &&
      !/^---+$/.test(lines[i].trim()) &&
      !/^(\*|-)\s+/.test(lines[i]) &&
      !/^\d+\.\s+/.test(lines[i]) &&
      !lines[i].trim().startsWith("|")
    ) {
      paragraphLines.push(lines[i]);
      i += 1;
    }

    blocks.push({ type: "p", text: paragraphLines.join(" ") });
  }

  return blocks;
};

const DeveloperDocs = () => {
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/docs/PAYSMART_CLIENT_API_DOCUMENTATION.md`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Documentation not found");
        }
        return response.text();
      })
      .then((text) => {
        setMarkdown(text);
        setLoading(false);
      })
      .catch(() => {
        setError("Unable to load documentation right now.");
        setLoading(false);
      });
  }, []);

  const blocks = useMemo(() => parseMarkdown(markdown), [markdown]);
  const headings = useMemo(
    () =>
      blocks
        .filter((block) => block.type === "heading")
        .map((block) => ({
          level: block.level,
          text: block.text,
          id: slugify(block.text),
        })),
    [blocks]
  );

  const renderBlock = (block, index) => {
    if (block.type === "heading") {
      const Tag = `h${block.level}`;
      const headingId = slugify(block.text);
      return (
        <Tag
          key={`heading-${index}`}
          id={headingId}
          className={`doc-heading level-${block.level}`}
          dangerouslySetInnerHTML={renderInlineHtml(block.text)}
        />
      );
    }

    if (block.type === "p") {
      return (
        <p
          key={`paragraph-${index}`}
          dangerouslySetInnerHTML={renderInlineHtml(block.text)}
        />
      );
    }

    if (block.type === "ul") {
      return (
        <ul key={`ul-${index}`}>
          {block.items.map((item, itemIndex) => (
            <li
              key={`ul-item-${index}-${itemIndex}`}
              dangerouslySetInnerHTML={renderInlineHtml(item)}
            />
          ))}
        </ul>
      );
    }

    if (block.type === "ol") {
      return (
        <ol key={`ol-${index}`}>
          {block.items.map((item, itemIndex) => (
            <li
              key={`ol-item-${index}-${itemIndex}`}
              dangerouslySetInnerHTML={renderInlineHtml(item)}
            />
          ))}
        </ol>
      );
    }

    if (block.type === "hr") {
      return <hr key={`hr-${index}`} />;
    }

    if (block.type === "code") {
      return (
        <pre key={`code-${index}`}>
          <code
            className={block.language ? `lang-${block.language}` : undefined}
            dangerouslySetInnerHTML={{ __html: escapeHtml(block.content) }}
          />
        </pre>
      );
    }

    if (block.type === "table") {
      return (
        <div key={`table-${index}`} className="doc-table">
          <table>
            <thead>
              <tr>
                {block.header.map((cell, cellIndex) => (
                  <th
                    key={`th-${index}-${cellIndex}`}
                    dangerouslySetInnerHTML={renderInlineHtml(cell)}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {block.body.map((row, rowIndex) => (
                <tr key={`tr-${index}-${rowIndex}`}>
                  {row.map((cell, cellIndex) => (
                    <td
                      key={`td-${index}-${rowIndex}-${cellIndex}`}
                      dangerouslySetInnerHTML={renderInlineHtml(cell)}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="tabled developer-docs-page">
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Card bordered={false} className="criclebox mb-24 developer-docs-hero">
            <Text className="developer-docs-kicker">Developers</Text>
            <Title level={2} className="developer-docs-title">
              PaySmart Client API Documentation
            </Title>
            <Paragraph className="developer-docs-subtitle">
              Everything you need to integrate with PaySmart services, from
              authentication to bulk operations.
            </Paragraph>
            <div className="developer-docs-meta">
              <span>Version: 1.0</span>
              <span>Last update: Dec 2025</span>
            </div>
            <a
              className="developer-docs-download"
              href={`${process.env.PUBLIC_URL}/docs/PaySmart.postman_collection.json`}
              download
            >
              <img src={postmanIcon} alt="" aria-hidden="true" />
              Download Postman Collection
            </a>
          </Card>
        </Col>
      </Row>
      <Row gutter={[24, 24]}>
        <Col xs={24} xl={6}>
          <Card bordered={false} className="criclebox developer-docs-toc">
            <Title level={5}>On this page</Title>
            {headings.length === 0 ? (
              <Paragraph type="secondary">No sections found.</Paragraph>
            ) : (
              <ul>
                {headings.map((heading, index) => (
                  <li
                    key={`toc-${index}`}
                    className={`toc-level-${heading.level}`}
                  >
                    <a href={`#${heading.id}`}>{heading.text}</a>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </Col>
        <Col xs={24} xl={18}>
          <Card bordered={false} className="criclebox developer-docs-content">
            {loading ? (
              <Skeleton active paragraph={{ rows: 12 }} />
            ) : error ? (
              <Paragraph type="danger">{error}</Paragraph>
            ) : (
              <div className="doc-markdown">
                {blocks.map((block, index) => renderBlock(block, index))}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DeveloperDocs;
